import React, { useState } from 'react';
import AptitudeOverview from './AptitudeOverview';
import AptitudeCompare from './AptitudeCompare';
import AptitudeHistoryViewer from './AptitudeHistoryViewer';
import AptitudeTimeline from './AptitudeTimeline';
import { trendVerdict, num, pct, readinessMeta } from './aptitudeHelpers';
import './AptitudeAnalytics.css';

// Shared shell for the Aptitude Analytics module — a CONTINUOUS performance
// tracker with its own identity (gradient hero + pill tabs) distinct from the
// one-time Diagnostic report. Consumed by both the admin (View Student) and the
// student portal, fed the same { profile, history, trend } bundle.
//
// props:
//   profile  – aptitude_profile (Tier 2 rolling snapshot) or null
//   history  – aptitude_attempt_scores rows (Tier 1, full stored metrics)
//   trend    – per-metric series derived from history
//   subtitle – contextual line under the hero title (e.g. student name)

const TABS = [
  { id: 'overview', label: 'Overview',  icon: '📊' },
  { id: 'timeline', label: 'Timeline',  icon: '🧭' },
  { id: 'compare',  label: 'Compare',   icon: '⚖️' },
  { id: 'history',  label: 'History',   icon: '🗂️' },
];

const AptitudeAnalytics = ({ profile, history = [], trend = [], subtitle }) => {
  const [tab, setTab] = useState('overview');

  const attempts = profile?.total_attempts ?? history.length;
  const eriSeries = (trend || []).map((t) => t.eri);
  const verdict = trendVerdict(eriSeries);
  const readiness = readinessMeta(profile?.readiness_level);

  return (
    <div className="apt-page">
      {/* Hero — the continuous-performance identity banner */}
      <div className="apt-hero">
        <span className="apt-hero-eyebrow">⚡ Continuous Performance Analytics</span>
        <h1 className="apt-hero-title">Aptitude Analytics</h1>
        <p className="apt-hero-sub">
          {subtitle || 'Track growth across every aptitude attempt — not a one-time snapshot.'}
        </p>
        <div className="apt-hero-stats">
          <div className="apt-hero-stat"><div className="v">{attempts}</div><div className="l">Attempts</div></div>
          <div className="apt-hero-stat"><div className="v">{pct(profile?.current_eri)}</div><div className="l">Current ERI</div></div>
          <div className="apt-hero-stat"><div className="v">{profile?.best_eri != null ? `${Math.round(num(profile.best_eri))}%` : '—'}</div><div className="l">Best ERI</div></div>
          <div className="apt-hero-stat"><div className="v" style={{ fontSize: '1.1rem' }}>{readiness.label}</div><div className="l">Readiness</div></div>
          <div className="apt-hero-trend">{verdict.icon} {verdict.label}</div>
        </div>
      </div>

      {/* Pill tabs */}
      <div className="apt-tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`apt-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            <span>{t.icon}</span>{t.label}
            {t.id === 'history' && history.length > 0 && <span className="apt-tab-badge">{history.length}</span>}
          </button>
        ))}
      </div>

      {tab === 'overview' && <AptitudeOverview profile={profile} trend={trend} />}
      {tab === 'timeline' && <AptitudeTimeline history={history} profile={profile} />}
      {tab === 'compare'  && <AptitudeCompare history={history} />}
      {tab === 'history'  && <AptitudeHistoryViewer history={history} />}
    </div>
  );
};

export default AptitudeAnalytics;
